import { useRequest } from '@/api/request'
import MiiaFwConfig from '@/configs/config'
import { ERROR_CODES } from '@/constants/error/errorCode'
import type { GetOrganizationsRequestParam as GetOrganizationsParam } from '@/types/api/dupAuth/getOrganizationsRequestParam'
import type { GetOrganizationsResponseParam as GetOrganizationsResponse } from '@/types/api/dupAuth/getOrganizationsResponseParam'
import type { GetOrganizationUsersRequestParam as GetOrganizationUsersParam } from '@/types/api/dupAuth/getOrganizationUsersRequestParam'
import type { GetOrganizationUsersResponseParam as GetOrganizationUsersResponse } from '@/types/api/dupAuth/getOrganizationUsersResponseParam'
import type { SetAccountRequestParam as SetAccountParam } from '@/types/api/dupAuth/setAccountRequestParam'
import type { SetAccountResponseParam as SetAccountResponse } from '@/types/api/dupAuth/setAccountResponseParam'
import type { UpdateAccountRequestParam as UpdateAccountParam } from '@/types/api/dupAuth/updateAccountRequestParam'
import type { UpdateAccountResponseParam as UpdateAccountResponse } from '@/types/api/dupAuth/updateAccountResponseParam'
import type { ErrorCode } from '@/utils/globalError/entities/valueObjects/types'
import { GlobalError } from '@/utils/globalError/GlobalError'

export const useWithAuthApi = () => {
  const { request } = useRequest()

  /** DUP認証API（要認証）へリクエストする */
  const requestRunFileProcessorEcsApi = <T, U>(func: string, arg: T) => {
    return request<T, U>(MiiaFwConfig.config.authEndpointDup, '/with_auth', func, arg)
  }

  /**
   * DUP認証API（要認証） getOrganizationsを実行する
   * @param param リクエストパラメータ
   * @returns レスポンス
   */
  const callGetOrganizations = async (param: GetOrganizationsParam) => {
    const res = await requestRunFileProcessorEcsApi<
      GetOrganizationsParam,
      GetOrganizationsResponse
    >('getOrganizations', param)
    if ('error' in res) {
      throw new GlobalError(
        [ERROR_CODES._811010_API_EXECUTION_FAILED, res.error.errorCode as ErrorCode],
        { message: res.error.errorMessage }
      )
    }
    return res.result
  }

  /**
   * DUP認証API（要認証） getOrganizationUsersを実行する
   * @param param リクエストパラメータ
   * @returns レスポンス
   */
  const callGetOrganizationUsers = async (param: GetOrganizationUsersParam) => {
    const res = await requestRunFileProcessorEcsApi<
      GetOrganizationUsersParam,
      GetOrganizationUsersResponse
    >('getOrganizationUsers', param)
    if ('error' in res) {
      throw new GlobalError(
        [ERROR_CODES._811010_API_EXECUTION_FAILED, res.error.errorCode as ErrorCode],
        { message: res.error.errorMessage }
      )
    }
    return res.result
  }

  /**
   * DUP認証API（要認証） setAccountを実行する
   * @param param リクエストパラメータ
   * @returns レスポンス
   */
  const callSetAccount = async (param: SetAccountParam) => {
    const res = await requestRunFileProcessorEcsApi<SetAccountParam, SetAccountResponse>(
      'setAccount',
      param
    )
    if ('error' in res) {
      throw new GlobalError(
        [ERROR_CODES._811010_API_EXECUTION_FAILED, res.error.errorCode as ErrorCode],
        { message: res.error.errorMessage }
      )
    }
    return res.result
  }

  /**
   * DUP認証API（要認証） updateAccountを実行する
   * @param param リクエストパラメータ
   * @returns レスポンス
   */
  const callUpdateAccount = async (param: UpdateAccountParam) => {
    const res = await requestRunFileProcessorEcsApi<UpdateAccountParam, UpdateAccountResponse>(
      'updateAccount',
      param
    )
    if ('error' in res) {
      throw new GlobalError(
        [ERROR_CODES._811010_API_EXECUTION_FAILED, res.error.errorCode as ErrorCode],
        { message: res.error.errorMessage }
      )
    }
    return res.result
  }

  return { callGetOrganizations, callGetOrganizationUsers, callSetAccount, callUpdateAccount }
}
